import { idDeWorkspace } from "@ghm/domain";

export interface LocalWorkflow {
  id: string;
  name: string;
  description?: string;
  createdAt: string;
}

export const WORKFLOW_STORAGE_KEY = "ghm:workflows";
export const DEFAULT_LOCAL_WORKFLOW: LocalWorkflow = {
  id: "workspace-local",
  name: "Linha principal",
  description: "Workflow padrao do ambiente local",
  createdAt: new Date(0).toISOString(),
};

/**
 * Delega para o domínio de propósito.
 *
 * O id do setor vai para a URL e é o valor gravado em
 * `devices.current_workspace_id`. Enquanto esta função tinha regra própria, o
 * navegador e o servidor podiam gerar ids diferentes para o mesmo nome — e um
 * setor que a interface mostra e a API não encontra é exatamente o defeito que
 * a rota `/api/workspaces` veio resolver.
 */
export function createWorkflowId(name: string, existingIds: string[] = []): string {
  return idDeWorkspace(name, existingIds);
}

export function normalizeWorkflows(value: unknown): LocalWorkflow[] {
  if (!Array.isArray(value)) return [];

  const workflows = value.flatMap((candidate): LocalWorkflow[] => {
    if (!candidate || typeof candidate !== "object") return [];
    const item = candidate as Record<string, unknown>;
    const id = typeof item.id === "string" ? item.id.trim() : "";
    const name = typeof item.name === "string" ? item.name.trim() : "";
    if (!id || !name) return [];

    return [
      {
        id,
        name,
        description:
          typeof item.description === "string" && item.description.trim()
            ? item.description.trim()
            : undefined,
        createdAt:
          typeof item.createdAt === "string" && item.createdAt
            ? item.createdAt
            : new Date(0).toISOString(),
      },
    ];
  });

  return workflows;
}

export function loadLocalWorkflows(storage: Pick<Storage, "getItem">): LocalWorkflow[] {
  const raw = storage.getItem(WORKFLOW_STORAGE_KEY);
  if (!raw) return [DEFAULT_LOCAL_WORKFLOW];

  try {
    const workflows = normalizeWorkflows(JSON.parse(raw));
    return workflows.length > 0 ? workflows : [DEFAULT_LOCAL_WORKFLOW];
  } catch {
    return [DEFAULT_LOCAL_WORKFLOW];
  }
}

export function saveLocalWorkflows(
  storage: Pick<Storage, "setItem">,
  workflows: LocalWorkflow[],
): void {
  storage.setItem(WORKFLOW_STORAGE_KEY, JSON.stringify(normalizeWorkflows(workflows)));
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent("ghm:workflows-updated"));
  }
}

export function buildWorkflow(name: string, description: string, existing: LocalWorkflow[]): LocalWorkflow {
  const trimmedName = name.trim();
  return {
    id: createWorkflowId(trimmedName, existing.map((workflow) => workflow.id)),
    name: trimmedName,
    description: description.trim() || undefined,
    createdAt: new Date().toISOString(),
  };
}
